import { useState, useRef } from 'react'
import { FieldValues, UseFormReturn, Path } from 'react-hook-form'
import { isUsernameTaken } from '../../validators'
import { NameScope } from '@wellesley/openapi'

type HookProps<T extends FieldValues & { userName: string }> = {
  form: UseFormReturn<T>
  originalUsername?: string
  groupId?: string
  nameScope?: NameScope
}

type UseUniqueUsernameReturn = {
  isUserNameChecked: boolean
  handleUserNameChange: () => void
  handleUserNameBlur: () => Promise<void>
}

const useUniqueUsername = <T extends FieldValues & { userName: string }>({
  form,
  groupId,
  nameScope,
  originalUsername,
}: HookProps<T>): UseUniqueUsernameReturn => {
  const existingUsername = useRef(originalUsername ?? '').current
  const getUsername = (form: UseFormReturn<T>) => {
    return form.getValues('userName' as Path<T>) as string
  }

  const [isUserNameChecked, setIsUserNameChecked] = useState(false)
  const [lastCheckedUsername, setLastCheckedUsername] = useState(
    getUsername(form),
  )

  const handleUserNameChange = () => {
    setIsUserNameChecked(false)
  }

  const handleUserNameBlur = async () => {
    const username = getUsername(form)
    const trimmedUsername = username.trim()

    if (
      trimmedUsername === lastCheckedUsername ||
      trimmedUsername === existingUsername
    ) {
      return
    }

    const { exists, message } = await isUsernameTaken({
      username: trimmedUsername,
      groupId,
      nameScope: nameScope ?? 'USER',
    })

    if (exists) {
      form.setError('userName' as Path<T>, {
        message,
      })
    }

    setLastCheckedUsername(trimmedUsername)
    setIsUserNameChecked(true)
  }

  return {
    isUserNameChecked,
    handleUserNameChange,
    handleUserNameBlur,
  }
}

export default useUniqueUsername
